/**
 * Pembaca CSV sederhana yang menghormati tanda kutip.
 *
 * CSV dipilih daripada .xlsx untuk prototype karena tidak menambah dependensi
 * dan bisa diekspor dari Excel/Google Sheets dengan satu klik. Membaca .xlsx
 * langsung bisa menyusul tanpa mengubah alur wizard-nya.
 */

/**
 * Menebak pemisah kolom dari baris HEADER, bukan menerima koma dan titik-koma
 * sekaligus.
 *
 * Excel berbahasa Indonesia menyimpan CSV dengan pemisah `;`, jadi keduanya
 * memang harus didukung. Tapi menerima keduanya SEKALIGUS membuat titik-koma
 * di dalam sel ikut memecah kolom — dan kolom MoV justru memakai titik-koma
 * untuk memisahkan poin ("Portofolio; Wawancara"). Akibatnya poin kedua
 * terlempar ke kolom siluman lalu hilang, dan hanya satu poin yang terimpor.
 *
 * Header dipakai sebagai contoh karena isinya sudah pasti: tidak ada nama
 * kolom yang mengandung koma maupun titik-koma.
 */
function sniffDelimiter(text: string): ',' | ';' {
  let commas = 0;
  let semicolons = 0;
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (inQuotes) {
      continue;
    } else if (ch === ',') {
      commas++;
    } else if (ch === ';') {
      semicolons++;
    } else if (ch === '\n') {
      break;
    }
  }

  return semicolons > commas ? ';' : ',';
}

export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  const delimiter = sniffDelimiter(text.replace(/^﻿/, ''));

  // Buang BOM yang sering ikut saat Excel menyimpan sebagai CSV UTF-8.
  const input = text.replace(/^﻿/, '');

  for (let i = 0; i < input.length; i++) {
    const ch = input[i]!;

    if (inQuotes) {
      if (ch === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      row.push(field.trim());
      field = '';
    } else if (ch === '\n') {
      row.push(field.trim());
      rows.push(row);
      row = [];
      field = '';
    } else if (ch !== '\r') {
      field += ch;
    }
  }

  if (field || row.length) {
    row.push(field.trim());
    rows.push(row);
  }

  return rows.filter((r) => r.some((cell) => cell !== ''));
}

/** Memetakan header berkas ke nama kolom internal, toleran terhadap variasi penulisan. */
export function mapHeaders(
  headers: string[],
  aliases: Record<string, string[]>,
): Record<string, number> {
  const index: Record<string, number> = {};
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

  headers.forEach((header, i) => {
    const h = norm(header);
    for (const [field, options] of Object.entries(aliases)) {
      if (index[field] !== undefined) continue;
      if (options.some((o) => norm(o) === h)) index[field] = i;
    }
  });

  return index;
}

/**
 * Tidak ada kolom Kode di mana pun — kode digenerate sistem.
 *
 * Karena itu NAMA yang jadi penanda "data ini sudah ada": TBM dengan nama sama
 * dianggap TBM yang sama, dan PELITA memilih TBM-nya lewat nama juga. Kalau
 * tidak ada penanda sama sekali, mengimpor berkas yang sama dua kali akan
 * menggandakan seluruh isinya tanpa satu pun peringatan.
 */
export const TBM_ALIASES: Record<string, string[]> = {
  name: ['nama', 'nama tbm', 'nama komunitas', 'nama komunitas literasi', 'name'],
  region: ['desa', 'desa/nagari', 'nagari', 'kelurahan', 'wilayah', 'village'],
  angkatan: ['angkatan', 'batch'],
  joinedPeriod: ['bergabung', 'bulan bergabung', 'tahun bergabung', 'periode bergabung', 'joined'],
  socialMedia: ['media sosial', 'medsos', 'sosmed', 'instagram', 'social media'],
};

export const PELITA_ALIASES: Record<string, string[]> = {
  name: ['nama', 'nama pelita', 'nama pelita sli', 'name'],
  tbmName: ['nama tbm', 'tbm', 'asal komunitas', 'komunitas'],
  gender: ['jenis kelamin', 'jk', 'gender', 'l/p'],
  birthPlace: ['tempat lahir', 'tempatlahir', 'kota lahir'],
  birthDate: ['tanggal lahir', 'tgl lahir', 'tanggallahir', 'lahir'],
  phone: ['no hp', 'nohp', 'telepon', 'hp', 'phone'],
  email: ['email', 'surel', 'e-mail'],
  personalBranding: ['personal branding', 'branding', 'keahlian', 'catatan', 'personal_branding'],
  profession: ['profesi', 'pekerjaan', 'jabatan', 'profession', 'job'],
  lastEducation: ['pendidikan terakhir', 'pendidikan', 'pendidikan_terakhir', 'education', 'last_education', 'lulusan'],
};

export const TBM_TEMPLATE =
  'Nama TBM,Desa,Angkatan,Bulan Bergabung,Media Sosial\n' +
  'TBM Tunas Bangsa,Nagari Paninjawan,Angkatan 1,2024-01,@tbmtunasbangsa\n';

export const PELITA_TEMPLATE =
  'Nama,Nama TBM,Jenis Kelamin,Tempat Lahir,Tanggal Lahir,No HP,Email,Personal Branding,Profesi,Pendidikan Terakhir\n' +
  'Rina Sari,TBM Tunas Bangsa,P,Solok,1995-04-17,081234567890,rina@contoh.id,Pegiat Literasi Anak & Pendongeng,Guru,S1\n';
